import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { useNavigate } from "react-router";
import {
  Code,
  Download,
  LayoutDashboard,
  Map,
  Pencil,
  Plus,
  Trash,
  FileSpreadsheet,
  FileText,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Rol } from "@/types/roles";
import { apiFetch } from "@/service/api";
import { getSessionItem } from "@/utils/storage";

interface Survey {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string;
}

export default function ListarEncuestas() {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [surveyLink, setSurveyLink] = useState("");
  const [copied, setCopied] = useState(false);
  const url_survey = import.meta.env.VITE_API_URL_SURVEY;
  const rolId: number = user.rol;
  const canDelete = rolId !== Rol.COLABORADOR && rolId !== Rol.ASISTENTE;
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [surveyToDownload, setSurveyToDownload] = useState<Survey | null>(null);

  // Estados para flujo de Word
  const [wordStep, setWordStep] = useState<
    "select" | "loading" | "options" | "empty"
  >("select");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rangeFrom, setRangeFrom] = useState(1);
  const [rangeTo, setRangeTo] = useState(10);
  const [downloadAll, setDownloadAll] = useState(true);

  const fetchSurveys = async () => {
    try {
      const response = await apiFetch(`survey/find-all`);
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
      }
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponder = (id: number) => {
    const tenantId = getSessionItem("tenantId");
    const link = `${url_survey}${tenantId}/${id}`;
    setSurveyLink(link);
    setIsLinkModalOpen(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(surveyLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch (err) {
      console.error("Error copiando enlace", err);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleDelete = (id: number) => {
    setSurveyToDelete(id);
    setIsModalOpen(true);
  };

  const handleOpenDownloadModal = (item: Survey) => {
    setSurveyToDownload(item);
    setWordStep("select");
    setTotalCount(0);
    setRangeFrom(1);
    setRangeTo(10);
    setDownloadAll(true);
    setIsDownloadModalOpen(true);
  };

  const handleDowloadExcel = async () => {
    if (!surveyToDownload) return;
    try {
      const response = await apiFetch(
        `survey-response/download/${surveyToDownload.id}?tipo=excel`,
        { method: "GET" },
      );
      if (!response.ok) throw new Error("Error al descargar el archivo");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("content-disposition");
      let fileName = `respuestas_encuesta_${surveyToDownload.id}.xlsx`;
      if (contentDisposition && contentDisposition.includes("attachment")) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1])
          fileName = matches[1].replace(/['"]/g, "");
      }
      if (blob.type === "application/zip" || fileName.endsWith(".zip")) {
        if (!fileName.endsWith(".zip"))
          fileName = fileName.replace(".xlsx", ".zip");
      }
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setIsDownloadModalOpen(false);
    } catch (error) {
      console.error("Error downloading survey responses:", error);
    }
  };

  const handleCountWord = async () => {
    if (!surveyToDownload) return;
    try {
      setWordStep("loading");
      const response = await apiFetch(
        `survey-response/download/${surveyToDownload.id}?tipo=count`,
      );
      if (response.ok) {
        const data = await response.json();
        const total = Number(data.total ?? data) || 0;
        setTotalCount(total);
        if (!total || total === 0) {
          setWordStep("empty");
        } else {
          setRangeFrom(1);
          setRangeTo(Math.min(10, total));
          setDownloadAll(total <= 100);
          setWordStep("options");
        }
      } else {
        setWordStep("empty");
      }
    } catch (error) {
      console.error("Error :", error);
      setWordStep("empty");
    }
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = parseInt(e.target.value);
    if (isNaN(val)) {
      setRangeFrom(1);
      return;
    }
    if (val < 1) val = 1;
    if (val > totalCount) val = totalCount;
    if (val > rangeTo) val = rangeTo;
    setRangeFrom(val);
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = parseInt(e.target.value);
    if (isNaN(val)) {
      setRangeTo(1);
      return;
    }
    if (val < 1) val = 1;
    if (val > totalCount) val = totalCount;
    if (val < rangeFrom) val = rangeFrom;
    setRangeTo(val);
  };

  const handleDownloadWordFinal = async () => {
    if (!surveyToDownload) return;
    if (!downloadAll) {
      if (
        rangeFrom < 1 ||
        rangeTo < 1 ||
        rangeFrom > rangeTo ||
        rangeTo > totalCount
      ) {
        alert(`Rango inválido. Debe estar entre 1 y ${totalCount}`);
        return;
      }
    }
    try {
      const query = `tipo=word&todo=${downloadAll}&desde=${rangeFrom}&hasta=${rangeTo}`;
      const response = await apiFetch(
        `survey-response/download/${surveyToDownload.id}?${query}`,
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        alert(errData?.message || `Error ${response.status}`);
        return;
      }

      const blob = await response.blob();
      const fileName =
        response.headers
          .get("Content-Disposition")
          ?.split("filename=")[1]
          ?.replace(/"/g, "") || `encuesta_${surveyToDownload.id}.docx`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setIsDownloadModalOpen(false);
    } catch (e) {
      console.error(e);
      alert("Error descargando el Word");
    }
  };

  const confirmDelete = async () => {
    if (!surveyToDelete) return;
    try {
      const response = await apiFetch(`survey/remove/${surveyToDelete}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setSurveys(surveys.filter((s) => s.id !== surveyToDelete));
        setIsModalOpen(false);
        setSurveyToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting survey:", error);
    }
  };

  const columns: Column<Survey>[] = [
    { label: "Codigo", key: "codigo" },
    { label: "Nombre", key: "nombre" },
    {
      label: "Descripcion",
      key: "descripcion",
      render: (value: any) => {
        if (!value) return "N/A";
        const text = value.length > 30 ? `${value.slice(0, 30)}...` : value;
        return (
          <span title={value} className="cursor-help">
            {text}
          </span>
        );
      },
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () =>
                navigate(`/gestion_proyectos/survey/update/${row.id}`),
            },
            {
              label: "Dashboard",
              icon: <LayoutDashboard className="size-4" />,
              onClick: () => navigate(`/survey/dashboard/${row.id}`),
            },
            {
              label: "Template",
              icon: <Code className="size-4" />,
              onClick: () => navigate(`/survey/template/${row.id}`),
            },
            {
              label: "Mapa de respuestas",
              icon: <Map className="size-4" />,
              onClick: () =>
                navigate(`/gestion_proyectos/survey/map/${row.id}`),
              className: "text-orange-500 hover:bg-red-50",
            },
            {
              label: "Descargar respuestas",
              icon: <Download className="size-4" />,
              onClick: () => handleOpenDownloadModal(row),
              className: "text-blue-500 hover:bg-red-50",
            },
            {
              label: "Responder",
              icon: <Plus className="size-4" />,
              onClick: () => handleResponder(row.id),
              className: "text-green-500 hover:bg-green-50",
            },
            ...(canDelete
              ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                    className: "text-red-500 hover:bg-red-50",
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Listado de Encuestas" />
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between mb-7">
          <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90">
            Encuestas Registradas
          </h3>
          <Button
            variant="primary"
            size="sm"
            startIcon={<Plus className="size-4" />}
            onClick={() => navigate("/gestion_proyectos/survey/create")}
          >
            Nueva Encuesta
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={surveys} />
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- w-full p-6"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/10">
            <Trash className="size-8" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h3>
          <p className="mb-7 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar esta encuesta? Esta accion no
            se puede deshacer.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700!border-red-600"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isFullscreen={false}
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        className="max-w- w-full p-6"
      >
        <div className="text-center w-full">
          <h3 className="mb-4 text-xl font-bold text-gray-800 dark:text-white/90">
            Responder Encuesta
          </h3>
          <p className="mb-4 text-sm text-gray-500 break-all">{surveyLink}</p>
          <div className="flex flex-col gap-3 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleCopyLink}
              >
                {copied ? "¡Copiado!" : "Copiar enlace"}
              </Button>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => window.open(surveyLink, "_blank")}
              >
                Abrir pestaña
              </Button>
            </div>
            {copied && (
              <span className="text-xs text-green-600 w-full text-center animate-pulse">
                Enlace copiado en el portapapeles
              </span>
            )}
          </div>
        </div>
      </Modal>

      {/* MODAL DESCARGAR - EXCEL / WORD */}
      <Modal
        isFullscreen={false}
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        className="max-w- w-full p-6"
      >
        {wordStep === "select" && (
          <div className="text-center">
            <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white/90">
              Descargar respuestas
            </h3>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Selecciona el formato de descarga
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={handleDowloadExcel}
                className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white p-6 hover:bg-gray-50 hover:border-green-500 transition-all group dark:border-gray-700 dark:bg-white/[0.03]"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="size-8" />
                </div>
                <span className="font-semibold text-gray-700 dark:text-white/90">
                  Excel
                </span>
                <span className="text-xs text-gray-400">.xlsx</span>
              </button>
              <button
                onClick={handleCountWord}
                className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white p-6 hover:bg-gray-50 hover:border-blue-500 transition-all group dark:border-gray-700 dark:bg-white/[0.03]"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-600 group-hover:scale-110 transition-transform">
                  <FileText className="size-8" />
                </div>
                <span className="font-semibold text-gray-700 dark:text-white/90">
                  Word
                </span>
                <span className="text-xs text-gray-400">.docx</span>
              </button>
            </div>
          </div>
        )}

        {wordStep === "loading" && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Contando respuestas...</p>
          </div>
        )}

        {wordStep === "empty" && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <AlertCircle className="size-8" />
            </div>
            <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white/90">
              Sin respuestas
            </h3>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              No se puede descargar en Word porque esta encuesta no tiene
              respuestas registradas. Total: {totalCount}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setWordStep("select")}
              >
                Volver
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => setIsDownloadModalOpen(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}

        {wordStep === "options" && (
          <div>
            <h3 className="mb-1 text-xl font-bold text-gray-800 dark:text-white/90 text-center">
              Descargar Word
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400 text-center">
              Total respuestas: <b>{totalCount}</b> - Elige el rango
            </p>
            <div className="mb-5 rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={downloadAll}
                  onChange={(e) => setDownloadAll(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white/90">
                  Descargar todo ({totalCount} registros)
                </span>
              </label>
              {!downloadAll && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                      Desde
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={totalCount}
                      value={rangeFrom}
                      onChange={handleFromChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                      Hasta
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={totalCount}
                      value={rangeTo}
                      onChange={handleToChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              )}
              {!downloadAll && (
                <p className="mt-2 text- text-gray-400">
                  Ej: 1 al 10, luego 10 al 20, etc.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setWordStep("select")}
              >
                Atrás
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleDownloadWordFinal}
              >
                Descargar Word
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
